<template>
    <div>
        <div id="scrollTop" class="filters-wrapper">
            <section class="primary-filters">
                <v-select
                    v-model="selections.category"
                    label="name"
                    :options="filters.categories"
                    placeholder="Category"
                    :searchable="true"
                />

                <v-select
                    v-if="filters.categories && filters.subcategories && filters.subcategories.length >= 1"
                    v-model="selections.subcategory"
                    label="name"
                    :options="filters.subcategories"
                    placeholder="Subcategory"
                    :searchable="true"
                />

                <div v-if="filters.telehealth_physician" class="checkbox">
                    <input id="checkbox" v-model="selections.telehealth_physician" type="checkbox" value="true" />
                    <label for="checkbox">Scheduling Virtual Visits</label>
                </div>

                <div class="or">- or -</div>

                <provider-search />

                <a href="#" class="clear-filters" @click.prevent="clear">Clear Filters</a>
            </section>

            <section class="secondary-filters">
                <h4>Filter Results</h4>

                <v-select
                    v-model="selections.region"
                    label="region_name"
                    :options="filters.regions"
                    placeholder="Regions"
                    class="region-filter"
                >
                    <template #selected-option="option">
                        <div v-html="option.region_name"></div>
                    </template>
                    <template #option="option">
                        <div v-html="option.region_name"></div>
                    </template>
                </v-select>

                <v-select
                    v-model="selections.location"
                    label="building_name"
                    :options="filters.locations"
                    placeholder="Locations"
                    class="location-filter"
                >
                    <template #selected-option="option">
                        <div v-html="option.building_name"></div>
                    </template>
                    <template #option="option">
                        <div v-html="option.building_name"></div>
                    </template>
                </v-select>

                <v-select
                    v-model="selections.gender"
                    label="name"
                    :options="filters.genders"
                    placeholder="Gender"
                />

                <v-select
                    v-model="selections.language"
                    label="name"
                    :options="filters.languages"
                    placeholder="Additional Languages"
                />

                <!-- <v-select
                    v-model="selections.tier"
                    label="name"
                    :options="filters.tiers"
                    placeholder="Tier"
                /> -->
            </section>
            <section v-if="help.heading || help.text" class="need-help">
                <h5 class="heading">{{ help.heading }}</h5>
                <p v-html="help.text"></p>
            </section>

            <section class="my-chart">
                <h2>My Chart</h2>
                <hr>
                <p>The Queen's Health System wants you to be involved in your health and medical care. MyChart makes this possible through our online portal or mobile app.</p>
                <a href="https://mychart.queens.org/MyChart/Authentication/Login?" target="_blank">
                    Sign up or log in <i aria-hidden="true" class="fas fa-chevron-right"></i>
                </a>
            </section>
        </div>
    </div>
</template>

<script>
import {mapState} from 'vuex';
import VSelect from 'vue-select';
import ProviderSearch from './ProviderSearch.vue';

export default {
    components: {VSelect, ProviderSearch},

    props: {
        help: {type: Object, required: true},
    },

    data() {
        return {
            selections: this.newSelections(),
            count: 0,
        };
    },

    computed: {
        ...mapState(['filters', 'totalProviders']),

        routeQuery() {
            return Object.keys(this.selections).reduce((params, key) => {
                params[key] = this.getSelectionValue(key);

                return params;
            }, {});
        },
    },

    watch: {
        totalProviders() {
            this.countUp();
        },
    },

    async created() {
        this.$store.dispatch('updateFilterSelections', this.$route.query);
        await this.$store.dispatch('getProviderFilters', this.$route.query);
        this.selections = this.newSelections(this.$route.query);

        this.$watch('selections.category', function() {
            this.selections.subcategory = null;

            const selections = this.newSelections();
            Object.keys(selections).forEach(key => {
                if (key != 'category') {
                    this.selections[key] = selections[key];
                }
            });
        });

        this.$watch('selections.region', function(newRegion, oldRegion) {
            const newRegionId = newRegion ? newRegion.id : null;
            const oldRegionId = oldRegion ? oldRegion.id : null;

            if (newRegionId !== oldRegionId) {
                this.selections.location = null;
            }
        });

        this.$watch('selections', {
            handler() {
                this.submit();
            },
            deep: true,
        });
    },

    methods: {
        clear() {
            this.selections = this.newSelections();
        },

        newSelections(query = {}) {
            return {
                // tier: this.selectionFromQuery('tiers', query.tier),
                category: this.selectionFromQuery('categories', query.category),
                subcategory: this.selectionFromQuery('subcategories', query.subcategory),
                telehealth_physician: query.telehealth_physician || false,
                accepting_new_patients: query.accepting_new_patients || false,
                region: this.selectionFromQuery('regions', query.region),
                location: this.selectionFromQuery('locations', query.location),
                gender: this.selectionFromQuery('genders', query.gender),
                language: this.selectionFromQuery('languages', query.language),
                insurance: this.selectionFromQuery('insurances', query.insurance),
            };
        },

        selectionFromQuery(key, value) {
            if (!value) {
                return null;
            }

            return this.filters[key].find(option => option.id == value);
        },

        getSelectionValue(key) {
            if (typeof this.selections[key] === 'boolean') {
                return this.selections[key] || null;
            }

            return this.selections[key] && this.selections[key].id;
        },

        formatNumber(number) {
            return number.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
        },

        countUp() {
            const animationDuration = 1000;
            const frameDuration = 1000 / 60;
            const totalFrames = Math.round(animationDuration / frameDuration);
            const easeOutQuad = t => t * (2 - t);
            let frame = 0;
            const countTo = this.totalProviders;

            const counter = setInterval(() => {
                frame++;
                const progress = easeOutQuad(frame / totalFrames);
                const currentCount = Math.round(countTo * progress);

                if (this.count !== currentCount) {
                    this.count = currentCount;
                }

                if (frame === totalFrames) {
                    clearInterval(counter);
                }
            }, frameDuration);
        },

        submit() {
            this.$router.push({path: this.basePath, query: this.routeQuery});
            this.$store.dispatch('updateFilterSelections', this.routeQuery);
            this.$store.dispatch('getProviderFilters', this.routeQuery);
            this.$store.dispatch('getProviders', this.routeQuery);
        },
    },
};
</script>
